
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { resendConfirmationEmail, attemptManualVerification, getAuthErrorMessage } from "./AuthUtils";

interface ConfirmationFormProps {
  switchToMode: (mode: string) => void;
  email: string;
  setEmail?: (email: string) => void;
}

const ConfirmationForm = ({ switchToMode, email, setEmail }: ConfirmationFormProps) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();

  const handleResendConfirmation = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    const success = await resendConfirmationEmail(email);
    setLoading(false);
    
    if (success) {
      // Set a 30 second cooldown to prevent spam
      setResendCooldown(30);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  const handleManualVerification = async () => {
    if (!password) {
      toast.error("Please enter your password to sign in");
      return;
    }
    
    setLoading(true);
    const result = await attemptManualVerification(email, password);
    setLoading(false);
    
    if (result.success) {
      navigate("/dashboard");
    }
  };

  return (
    <div className="space-y-4 text-center">
      <p className="text-sm text-clustr-darkGray">
        We've sent a confirmation email to <span className="font-medium">{email}</span>
      </p>
      <p className="text-sm text-clustr-darkGray">
        Please check your inbox and spam folder, then click the confirmation link to complete your registration.
      </p>
      <p className="text-sm text-clustr-darkGray">
        Didn't receive an email? Click below to resend.
      </p>
      <Button
        onClick={handleResendConfirmation}
        className="w-full h-12 bg-clustr-blue hover:bg-blue-600 transition-colors"
        disabled={loading || resendCooldown > 0}
      >
        {loading ? "Sending..." : 
         resendCooldown > 0 ? `Resend available in ${resendCooldown}s` : 
         "Resend Confirmation Email"}
      </Button>
      
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-clustr-lightGray"></span>
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-2 bg-white text-clustr-darkGray">Or Try to Sign In</span>
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="verification-password" className="text-left block">Password</Label>
          <Input
            id="verification-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            className="h-12"
          />
        </div>
        
        <Button
          onClick={handleManualVerification}
          className="w-full h-12"
          disabled={loading}
        >
          {loading ? "Checking..." : "Sign In"}
        </Button>
      </div>
    </div>
  );
};

export default ConfirmationForm;
